/**
 * `04/ Testing` — the cross-CRF test-round register at `/countries/:cc/testing`.
 *
 * ===========================================================================
 * FAILED ROUNDS ARE WHAT THIS SCREEN IS FOR
 * ===========================================================================
 * Every other view of a test round lives INSIDE one work order, which is the
 * right shape for somebody working one job and the wrong shape for the question
 * this register answers: "what failed in this country, and why?". So the design
 * of the row is bent towards that reader —
 *
 *   · the result pill carries the DANGER pair on a failure (`bg-danger-bg
 *     text-danger-fg`, the same pair `LifecyclePanels` already paints a failed
 *     round with) and the success/neutral pairs otherwise, so a page of rows
 *     can be scanned for red without reading a word;
 *   · a failed round's NOTE is rendered WHOLE and in that same danger pair
 *     rather than truncated into the column, because the note is the reason and
 *     a reason clipped at 40 characters is a reason nobody can act on;
 *   · a failure that carries NO note says so in a sentence. It is a real and
 *     unhelpful state ("a failure without one helps nobody" — the round-entry
 *     form says as much), and an empty cell would read as a rendering bug.
 *
 * No colour is invented here: every class pair below already exists in
 * `domain/quotes.ts`, `domain/stages.ts` or `ConfirmationEntryPage`.
 *
 * ===========================================================================
 * `?result=` IS PASSED TO THE SERVER EXACTLY AS THE URL HOLDS IT
 * ===========================================================================
 * The server owns the vocabulary and answers 400 naming it. This screen does not
 * pre-filter the parameter, because a hand-typed `?result=bogus` that was
 * silently dropped would render the WHOLE register under a URL claiming to be
 * filtered — and on the one screen whose job is "show me the failures", an
 * unfiltered list that looks filtered is the worst available answer. The
 * server's sentence is surfaced instead. See `api.ts`.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { useCountryCode } from '../../auth/guards';
import { useSession } from '../../auth/session';
import { PageHeader } from '../../components/shell/AppShell';
import { Alert, EmptyState, SkeletonRows } from '../../components/ui';
import { relativeTime } from '../../domain/events';
import { formatDateLong } from '../inquiries/format';
import { cn } from '../../lib/cn';
import { fetchTestingRegister, registerErrorText, registerKeys } from './api';
import type { TestRoundRegisterRow, TestingRegisterFilters } from './api';

const PER_PAGE = 50;

/**
 * The filter chips, in the enum's own order with "All" in front. `value: null`
 * is the absence of the parameter, not a fourth result.
 */
const RESULT_FILTERS: readonly { value: string | null; label: string }[] = [
  { value: null, label: 'All' },
  { value: 'in_test', label: 'In test' },
  { value: 'pass', label: 'Pass' },
  { value: 'fail', label: 'Fail' },
];

/**
 * `Pill/Stage` geometry with the status tone pairs. Keyed by `string` rather
 * than by the union so an unrecognised result renders as an honest neutral chip
 * carrying the SERVER'S OWN WORD — the `CrfStagePill` rule: never a blank pill,
 * never a guessed colour.
 */
const RESULT_PILL: Readonly<Record<string, { label: string; classes: string }>> = {
  in_test: { label: 'In test', classes: 'bg-info-bg text-info-fg' },
  pass: { label: 'Pass', classes: 'bg-ok-bg text-ok-fg' },
  fail: { label: 'Fail', classes: 'bg-danger-bg text-danger-fg' },
};

function resultPill(result: string): { label: string; classes: string } {
  return RESULT_PILL[result] ?? { label: result, classes: 'bg-subtle text-fg ring-1 ring-line' };
}

export function TestingRegisterPage(): React.JSX.Element {
  const countryCode = useCountryCode();
  const { can } = useSession();
  const [params, setParams] = useSearchParams();

  const canView = can(countryCode, 'crf.view');

  const rawResult = params.get('result');
  const result = rawResult === null || rawResult === '' ? null : rawResult;
  const page = Math.max(1, Number(params.get('page') ?? '1') || 1);

  const filters = useMemo<TestingRegisterFilters>(
    () => ({ result: result ?? undefined, page, perPage: PER_PAGE }),
    [result, page],
  );

  const list = useQuery({
    queryKey: registerKeys.testing(countryCode, filters),
    queryFn: ({ signal }) => fetchTestingRegister(countryCode, filters, signal),
    enabled: canView,
  });

  if (!canView) {
    // D5 — the refusal is a designed state naming who to ask, not a blank screen
    // and not a table rendered empty (which would read as "nothing failed").
    return (
      <div className="flex flex-col gap-4 p-6">
        <Alert tone="danger" title="Testing is not available to this account">
          Viewing the testing register in {countryCode} needs the CRF permission. Ask an
          administrator to widen your access.
        </Alert>
      </div>
    );
  }

  const rows = list.data?.data ?? [];
  const total = list.data?.total ?? 0;

  const setResult = (next: string | null): void => {
    const patch = new URLSearchParams(params);
    if (next === null) patch.delete('result');
    else patch.set('result', next);
    // A filter change resets paging — page 3 of the failures is not page 3 of
    // the whole register.
    patch.delete('page');
    setParams(patch, { replace: true });
  };

  const goToPage = (next: number): void => {
    const patch = new URLSearchParams(params);
    if (next <= 1) patch.delete('page');
    else patch.set('page', String(next));
    setParams(patch);
  };

  return (
    <div className="flex flex-col gap-4 p-6">
      <PageHeader
        title="Testing"
        subtitle={
          list.data === undefined
            ? undefined
            : `${String(list.data.total)} ${list.data.total === 1 ? 'test round' : 'test rounds'}`
        }
      />

      <div role="group" aria-label="Filter by result" className="flex flex-wrap items-center gap-2">
        {RESULT_FILTERS.map((chip) => (
          <ResultChip
            key={chip.label}
            label={chip.label}
            active={result === chip.value}
            onSelect={() => setResult(chip.value)}
          />
        ))}
      </div>

      {list.isPending ? (
        <div className="rounded-md border border-line bg-surface px-4">
          <SkeletonRows rows={6} label="Loading the testing register" />
        </div>
      ) : list.isError ? (
        <Alert tone="danger" title="The testing register could not be loaded">
          {/* The server's own sentence — on a 400 it names the vocabulary. */}
          <p>{registerErrorText(list.error)}</p>
          <button
            type="button"
            onClick={() => {
              void list.refetch();
            }}
            className="mt-1 font-medium underline underline-offset-2"
          >
            Try again.
          </button>
        </Alert>
      ) : rows.length === 0 && total > 0 ? (
        // An empty PAGE is not an empty RESULT SET — the quotes list's V3 lesson.
        <Alert tone="info" title={`Page ${String(list.data.page)} is past the end of this register`}>
          <p>
            The register holds {String(total)} {total === 1 ? 'test round' : 'test rounds'}.
          </p>
          <button
            type="button"
            onClick={() => goToPage(1)}
            className="mt-1 font-medium underline underline-offset-2"
          >
            Show the first page.
          </button>
        </Alert>
      ) : rows.length === 0 ? (
        result === null ? (
          <div className="rounded-md border border-dashed border-line bg-surface">
            <EmptyState title="No test rounds have been recorded in this country yet." />
          </div>
        ) : (
          <div className="rounded-md border border-dashed border-line bg-surface">
            <EmptyState
              title="No test round in this country has that result."
              action={
                <button
                  type="button"
                  onClick={() => setResult(null)}
                  className="text-body13 font-medium text-indigo hover:underline"
                >
                  Show every result.
                </button>
              }
            />
          </div>
        )
      ) : (
        <TestingTable countryCode={countryCode} rows={rows} />
      )}

      {/* Every paging number below comes off the one payload the rows came off. */}
      {list.data !== undefined && rows.length > 0 ? (
        <PagerFooter
          page={list.data.page}
          perPage={list.data.perPage}
          shown={rows.length}
          total={list.data.total}
          onPage={goToPage}
        />
      ) : null}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────── chips ── */

/** The Toolbar filter-chip geometry (6:467). No count: this route serves none. */
function ResultChip({
  label,
  active,
  onSelect,
}: {
  label: string;
  active: boolean;
  onSelect: () => void;
}): React.JSX.Element {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onSelect}
      className={cn(
        'flex h-8 items-center gap-1.5 rounded-sm border border-line bg-surface px-2.5 text-body13',
        active ? 'border-line-strong font-medium text-fg' : 'text-fg-secondary hover:text-fg',
      )}
    >
      {label}
    </button>
  );
}

/* ─────────────────────────────────────────────────────────────────── table ── */

const HEADERS = ['CRF', 'CUSTOMER', 'ROUND', 'RESULT', 'TESTER', 'NOTE'] as const;

function TestingTable({
  countryCode,
  rows,
}: {
  countryCode: string;
  rows: readonly TestRoundRegisterRow[];
}): React.JSX.Element {
  return (
    <div className="overflow-x-auto rounded-md border border-line bg-surface">
      <table className="w-full min-w-[1120px] border-collapse text-left">
        <caption className="sr-only">Every test round recorded in {countryCode}</caption>
        <colgroup>
          <col className="w-[170px]" />
          <col className="w-[170px]" />
          <col className="w-[80px]" />
          <col className="w-[120px]" />
          <col className="w-[150px]" />
          <col />
          <col className="w-[150px]" />
          <col className="w-[150px]" />
        </colgroup>

        <thead>
          <tr className="h-10 border-b border-line bg-subtle">
            {HEADERS.map((header) => (
              <th key={header} scope="col" className="px-3 text-th uppercase text-fg-muted">
                {header}
              </th>
            ))}
            {/* Server-ordered, newest first — stated, not a dead sort control. */}
            <th scope="col" className="px-3 text-th uppercase text-fg-muted">
              DATE <span aria-hidden="true">↓</span>
              <span className="sr-only">, newest first</span>
            </th>
            <th scope="col" className="px-3">
              <span className="sr-only">Row actions</span>
            </th>
          </tr>
        </thead>

        <tbody>
          {rows.map((row) => {
            const pill = resultPill(row.result);
            const failed = row.result === 'fail';
            return (
              <tr key={row.id} className="crf-register-row border-b border-line last:border-b-0 hover:bg-subtle">
                <td className="px-3 py-3 align-middle">
                  <Link
                    to={`/countries/${countryCode}/crfs/${row.crf_id}`}
                    className="font-mono text-mono text-fg underline underline-offset-2 hover:decoration-2"
                  >
                    {row.crf_doc_no}
                  </Link>
                </td>

                <td className="px-3 py-3 align-middle text-body13 text-fg">{row.party_name}</td>

                <td className="tnum px-3 py-3 align-middle text-body13 text-fg-secondary">
                  {String(row.round_no)}
                </td>

                <td className="px-3 py-3 align-middle">
                  <span
                    // Addressed by RESULT, never by row index: a test that asserts
                    // "the failure is red" must not retarget if the order changes.
                    data-result={row.result}
                    className={cn(
                      'inline-flex items-center gap-1.5 whitespace-nowrap rounded-pill px-2.5 py-1 text-label font-medium',
                      pill.classes,
                    )}
                  >
                    <span aria-hidden="true" className="size-1.5 shrink-0 rounded-full bg-current" />
                    {pill.label}
                  </span>
                </td>

                <td className="px-3 py-3 align-middle text-body13 text-fg">{row.tester.name}</td>

                <NoteCell failed={failed} note={row.note} />

                <td className="px-3 py-3 align-middle">
                  <div className="flex flex-col gap-0.5">
                    <span className="text-body13 text-fg">{formatDateLong(row.created_at)}</span>
                    <span className="text-label text-fg-muted">{relativeTime(row.created_at)}</span>
                  </div>
                </td>

                <td className="px-3 py-3 align-middle">
                  {/* The row's one action IS this link — a real anchor, so
                   * middle-click and copy-link-address work (the QuotesListPage
                   * precedent). It goes to the WORK ORDER, which is where the
                   * round was recorded and where the next one will be. */}
                  <Link
                    to={`/countries/${countryCode}/work/${row.work_order_id}`}
                    className="whitespace-nowrap text-body13 font-medium text-indigo hover:underline"
                  >
                    Open work order
                  </Link>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/**
 * The note, weighted by whether anyone needs to read it.
 *
 * A passing round's note is context and is truncated into the column with the
 * full text in `title`. A FAILING round's note is the reason and is rendered
 * whole, in the danger pair — see the file header. `title` carries the full text
 * in both cases so a hover always answers, truncated or not.
 */
function NoteCell({ failed, note }: { failed: boolean; note: string | null }): React.JSX.Element {
  if (note === null || note.trim() === '') {
    return (
      <td className="px-3 py-3 align-middle">
        <span className="text-label text-fg-muted">
          {failed ? 'No reason was recorded for this failure.' : '—'}
        </span>
      </td>
    );
  }
  if (failed) {
    return (
      <td className="px-3 py-3 align-middle">
        <span
          title={note}
          className="inline-block rounded-sm bg-danger-bg px-2 py-1 text-label text-danger-fg"
        >
          {note}
        </span>
      </td>
    );
  }
  return (
    <td className="max-w-0 px-3 py-3 align-middle">
      <span title={note} className="block truncate text-body13 text-fg-secondary">
        {note}
      </span>
    </td>
  );
}

/* ────────────────────────────────────────────────────────────────── pager ── */

/**
 * `Table/Pagination` — re-authored here rather than imported from
 * `CrfsListPage`, which keeps it file-private. Same house pattern as
 * `WorkConfirmationPage`'s `Pager`: the geometry is shared, the component is not.
 */
function PagerFooter({
  page,
  perPage,
  shown,
  total,
  onPage,
}: {
  page: number;
  perPage: number;
  shown: number;
  total: number;
  onPage: (page: number) => void;
}): React.JSX.Element {
  const firstOnPage = total === 0 ? 0 : (page - 1) * perPage + 1;
  const lastOnPage = (page - 1) * perPage + shown;

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-line bg-surface px-4 py-2">
      <p className="text-body13 text-fg-secondary">{perPage} per page</p>
      <div className="flex items-center gap-2">
        <p className="tnum text-body13 text-fg-secondary">
          {firstOnPage}–{lastOnPage} of {total}
        </p>
        <PageButton label="Previous page" disabled={page <= 1} onClick={() => onPage(page - 1)}>
          <ChevronLeft aria-hidden="true" className="size-4" />
        </PageButton>
        <PageButton
          label="Next page"
          disabled={lastOnPage >= total}
          onClick={() => onPage(page + 1)}
        >
          <ChevronRight aria-hidden="true" className="size-4" />
        </PageButton>
      </div>
    </div>
  );
}

function PageButton({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string;
  disabled: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className="flex size-7 items-center justify-center rounded-sm border border-line bg-surface text-fg-secondary hover:text-fg disabled:cursor-not-allowed disabled:opacity-40"
    >
      {children}
    </button>
  );
}
